import { FC, useEffect, useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";

import { useBandContext } from "Bands/contexts";
import { copy } from "shared/assets";
import { Download } from "shared/assets/icons";
import { Modal } from "shared/components";
import { Spacer } from "shared/layout";
import { IAPIState, ILocalStorageKey, IStatus } from "shared/types";

import { ExportXlsForm } from "Bands/components/ExportXlsForm/ExportXlsForm";
import { dataMetaSelector } from "Bands/state";
import { createBandExport } from "Bands/state/data/actions/bandExport";
import {
  BAND_ACTIONS,
  IBandXlsConfigParams,
  IEventStatus,
  PeopleBlockStatus,
} from "Bands/types";
import {
  eventStatuses,
  peopleBlockStatuses,
  useClearActionMetaOnUnMount,
} from "Bands/utils";
import { addTemporaryToast } from "shared/components/Toast";
import { LocalStorageService } from "shared/services";

const { calendar: calendarCopy } = copy;
interface ExportXlsModalProps {
  handleClose: () => void;
}

export const ExportXlsModal: FC<ExportXlsModalProps> = ({ handleClose }) => {
  const { api } = useSelector(dataMetaSelector(BAND_ACTIONS.createBandExport));
  const { bandId, eventTypes, infoBlockTypes } = useBandContext();

  useClearActionMetaOnUnMount(BAND_ACTIONS.createBandExport);

  const dispatch = useDispatch();

  const defaultValues = useMemo((): IBandXlsConfigParams => {
    return {
      eventTypes: eventTypes.map(({ id }) => id),
      eventStatuses: eventStatuses
        .filter(
          ({ value }) =>
            value !== IEventStatus.postponed && value !== IEventStatus.canceled
        )
        .map(({ value }) => value),
      eventRSVPStatuses: peopleBlockStatuses
        .map(({ value }) => value)
        .filter(
          (value) =>
            value !== PeopleBlockStatus.not_attending && value !== "none"
        ),
    };
  }, [eventTypes]);

  const handleSubmit = (data: IBandXlsConfigParams) => {
    LocalStorageService.setItem(ILocalStorageKey.xlsExportPreferencesPerBand, {
      ...LocalStorageService.getItem(
        ILocalStorageKey.xlsExportPreferencesPerBand
      ),
      [bandId!]: {
        ...data,
        startDate: undefined,
        endDate: undefined,
      },
    });

    const cleanData = {
      ...data,
      eventBlocks: data.eventBlocks?.map((blockType: any) => {
        if (typeof blockType === "string") {
          if (blockType.includes("event_info_block_types")) {
            return {
              blockType: "/api/event_info_block_types",
              eventInfoBlockType: blockType,
            };
          }

          return { blockType };
        }

        const eventBlockType = Object.keys(blockType)[0];

        return {
          blockType: eventBlockType,
          [blockType[eventBlockType as keyof typeof blockType]]: true,
        };
      }),
    };

    dispatch(
      createBandExport({
        data: {
          band: `/api/bands/${bandId!}`,
          exportType: "excel",
          config: cleanData,
        },
      })
    );
  };

  useEffect(() => {
    if (api?.status === IAPIState.succeeded) {
      handleClose();
      dispatch(
        addTemporaryToast({
          status: IStatus.success,
          message: "Export download started successfully",
        })
      );
    }
  }, [api, dispatch, handleClose]);

  return (
    <Modal
      icon={Download}
      title={calendarCopy.xlsExport.title}
      dataTestId="export_xls_modal"
      handleClose={handleClose}
      showCloseButton
    >
      <Spacer size="l">
        <ExportXlsForm
          defaultValues={
            LocalStorageService.getItem(
              ILocalStorageKey.xlsExportPreferencesPerBand
            )?.[bandId!] || defaultValues
          }
          bandId={bandId!}
          eventTypes={eventTypes}
          infoBlockTypes={infoBlockTypes}
          handleSubmit={handleSubmit}
          apiStatus={api?.status}
          hasError={api?.status === IAPIState.failed}
        />
      </Spacer>
    </Modal>
  );
};
